import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faArrowLeft, faSave, faSpinner, faToggleOn, faToggleOff, faCog, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import { ConfigurationsService, Configuration } from '../../services/configurations.service';
import { UsersService, UserPreferences, DEFAULT_USER_PREFERENCES } from '../../services/users.service';
import { TenantService } from '../../services/tenant.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { HeaderActionsComponent, HeaderActionsConfig } from '../../components/header-actions/header-actions.component';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'app-preferences',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    FontAwesomeModule,
    TranslateModule,
    HeaderActionsComponent,
    NgSelectModule
  ],
  templateUrl: './preferences.component.html',
  styleUrls: ['./preferences.component.scss']
})
export class PreferencesComponent implements OnInit {
  faArrowLeft = faArrowLeft;
  faSave = faSave;
  faSpinner = faSpinner;
  faToggleOn = faToggleOn;
  faToggleOff = faToggleOff;
  faCog = faCog;
  faGraduationCap = faGraduationCap;

  loading = false;
  saving = false;

  // theme/ui_language/max_simulation_files são controlados na tela de Configurações (settings),
  // não aqui — só carregamos e reenviamos o valor atual pra não sobrescrevê-lo ao salvar.
  preferences: UserPreferences = { ...DEFAULT_USER_PREFERENCES };

  aiModelLanguageOptions = [
    { value: 'pt', label: 'SETTINGS.PREFERENCES.AI_MODEL_LANGUAGE_OPTIONS.PT' },
    { value: 'en', label: 'SETTINGS.PREFERENCES.AI_MODEL_LANGUAGE_OPTIONS.EN' },
    { value: 'es', label: 'SETTINGS.PREFERENCES.AI_MODEL_LANGUAGE_OPTIONS.ES' }
  ];

  headerConfig: HeaderActionsConfig = {
    showBackButton: true,
    showAddButton: false,
    showSearch: false,
    showTagFilter: false,
    customButtons: [
      {
        type: 'custom',
        text: 'BUTTON.SAVE',
        icon: faSave,
        visible: true,
        disabled: false,
        customClass: 'btn-primary'
      }
    ]
  };

  delfosServerConfigs: Configuration[] = [];
  delfosLlmConfigs: Configuration[] = [];
  di2winocrServerConfigs: Configuration[] = [];
  di2winocrFeaturesConfigs: Configuration[] = [];

  constructor(
    private router: Router,
    private translate: TranslateService,
    private configurationsService: ConfigurationsService,
    private usersService: UsersService,
    private alertService: AlertService,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    
    this.configurationsService.listAllowedForMe().subscribe({
      next: (response) => {
        const configs = response.configurations || [];
        this.delfosServerConfigs = configs.filter(c => c.type === 'delfos_server');
        this.delfosLlmConfigs = configs.filter(c => c.type === 'delfos_llm');
        this.di2winocrServerConfigs = configs.filter(c => c.type === 'di2winocr_server');
        this.di2winocrFeaturesConfigs = configs.filter(c => c.type === 'di2winocr_features');
        
        this.loadUserPreferences();
      },
      error: (err) => {
        this.alertService.showError(err);
        this.loading = false;
      }
    });
  }

  loadUserPreferences(): void {
    this.usersService.getMe().subscribe({
      next: (response) => {
        if (response.user && response.user.preferences) {
          this.preferences = { ...this.preferences, ...response.user.preferences };
        }
        this.loading = false;
      },
      error: (err) => {
        this.alertService.showError(err);
        this.loading = false;
      }
    });
  }

  savePreferences(): void {
    this.saving = true;
    this.headerConfig.customButtons![0].disabled = true;

    this.usersService.updatePreferences(this.preferences).subscribe({
      next: () => {
        this.alertService.showSuccess(this.translate.instant('SETTINGS.PREFERENCES.SAVE_SUCCESS'));
        this.saving = false;
        this.headerConfig.customButtons![0].disabled = false;
      },
      error: (err) => {
        this.alertService.showError(err);
        this.saving = false;
        this.headerConfig.customButtons![0].disabled = false;
      }
    });
  }

  showConfig(type: 'delfos_server' | 'delfos_llm' | 'di2winocr_server' | 'di2winocr_features'): boolean {
    switch (type) {
      case 'delfos_server': return this.delfosServerConfigs.length > 1;
      case 'delfos_llm': return this.delfosLlmConfigs.length > 1;
      case 'di2winocr_server': return this.di2winocrServerConfigs.length > 1;
      case 'di2winocr_features': return this.di2winocrFeaturesConfigs.length > 1;
      default: return false;
    }
  }

  hasMultipleConfigs(): boolean {
    return this.delfosServerConfigs.length > 1 || 
           this.delfosLlmConfigs.length > 1 || 
           this.di2winocrServerConfigs.length > 1 || 
           this.di2winocrFeaturesConfigs.length > 1;
  }

  onHeaderAction(event: any): void {
    if (event.type !== 'custom') {
      return;
    }

    if (event.index === 0) {
      this.savePreferences();
    }
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/settings'));
  }
}
